import React, { useState, useEffect } from 'react';
import { Dialog, IconButton, Icon, List, ListItemButton, Checkbox, ListItemText, Collapse } from '@mui/material';
import { Button } from 'global/components/Button';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector, useDispatch } from 'react-redux';
import { get, set, size, uniqBy } from 'global/lib/lodash';
import Alert from '@mui/material/Alert';
import { getTaskStatus, getTaskImage, sortUsersAlphabetically } from 'global/utils';
import { Loader } from 'global/styles';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { updateTasksToLatest, setNewTasksToSent } from 'redux/actions/view';
import ReassignModal from './ReassignModal';
import {
  StyledAppBar,
  StyledToolbar,
  Title,
  Container,
  NotifyList,
  SelectAll,
  StyledListItem,
  AssigneeText,
  BarTitle,
  ButtonWrap,
  ButtonRow,
  SecondaryAction,
  SecondaryButton,
  ChildList,
  ImageIconWrap,
  CheckboxIcon,
  NOTIFY_WHITE_ICON,
} from './NotifyModal.styles';
import { useUser } from 'global/hooks/useUser';
import Image from 'global/components/image'

export default (props) => {
  const [error, setError] = useState();
  const [userMap, setUserMap] = useState({});
  const [assignees, setAssignees] = useState([]);
  const [reAssignUserList, reAssignUserListUpdate] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [reModal, _reModal] = useState({ open: false });
  const dispatch = useDispatch();

  const { me } = useUser();
  const globalRealm = useSelector((state) => get(state, 'auth.realm.id'));
  const params = props.hasOwnProperty('realmId')
    ? {
        waitFor: props.realmId,
        params: { filter: { content_realm_id: props.realmId } },
        useCache: { reload: 1 },
      }
    : {
        waitFor: globalRealm,
        params: { filter: { content_realm_id: globalRealm ? globalRealm : get(me, 'realm.id') } },
        useCache: { reload: 1 },
      };
  const usersList = useRetrieveData({ url: '/users' }, { useEffect: true, build: ['users'], ...params });

  useEffect(() => {
    if (props.open) {
      if (!size(props.unsentTasks.data)) {
        return handleClose();
      }

      reAssignUserListUpdate(uniqBy([me, ...(usersList.data || [])], 'id'));
      const _userMap = {};

      const userGroups = get(props, 'unsentTasks.name.userGroups', {});
      Object.keys(userGroups).map((key) => {
        const group = userGroups[key];

        set(_userMap, [key], {
          ...group,
          selectedTasks: [],
          open: true,
          key,
        });
      });
      setAssignees(Object.keys(_userMap).map((key) => ({ ...get(_userMap, key) })));
      setUserMap(_userMap);
    }
  }, [props.open, props.unsentTasks]);

  const handleClose = () => {
    props.onClose();
  };

  const getSelectedTasks = () => {
    let tids = [];
    Object.keys(userMap).forEach((k) => {
      tids = [...tids, ...get(userMap, [k, 'selectedTasks'], [])];
    });
    return get(props, 'unsentTasks.data', []).filter((t) => tids.indexOf(t.id) > -1);
  };

  const reassign = async () => {
    const tasks = getSelectedTasks();

    _reModal({
      open: true,
      title: 'Re-Assign Tasks',
      tasks,
      assignees: sortUsersAlphabetically(reAssignUserList),
      onClose: async (update) => {
        try {
          setLoading(true);
          _reModal({ open: false });
          if (update) {
            await dispatch(updateTasksToLatest());
          }
          setLoading(false);
        } catch (err) {
          console.log(err);
          setLoading(false);
        }
      },
    });
  };

  const dontNotify = () => {
    const obj = {
      tasksSent: [],
    };

    Object.keys(userMap).forEach((k) => {
      const selTasks = get(userMap, [k, 'selectedTasks']);
      if (size(selTasks)) {
        obj.tasksSent = [...obj.tasksSent, ...selTasks];
      }
    });

    props.actions.dontNotify(obj.tasksSent);
  };

  const stopProp = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const share = () => {
    const selectedTasks = getSelectedTasks();
    get(props, 'actions.share', () => 0)(selectedTasks);
  };

  const notify = async () => {
    if (sending) return;
    setSending(true);
    const obj = {
      users: [],
      tasksSent: [],
      emails: 0,
      tasks: 0,
    };

    Object.keys(userMap).forEach((k) => {
      const selTasks = get(userMap, [k, 'selectedTasks']);
      if (size(selTasks)) {
        obj.users = [...obj.users, ...get(userMap, [k, 'assignees'], [])];
        obj.emails += 1;
        obj.tasksSent = [...obj.tasksSent, ...selTasks];
        obj.tasks += size(selTasks);
      }
    });

    const getName = () => {
      const others = size(obj.users) - 1;
      return `${get(obj, ['users', 0, 'name'])}` + (size(obj.users) > 1 ? ` and ${others} ${others > 1 ? 'others' : 'other'}` : '');
    };

    const messages = await dispatch(setNewTasksToSent(obj.tasksSent));

    setSending(false);

    props.actions.setAlert({
      message: `${size(messages)} emails of ${obj.tasks} tasks sent to ${getName()}.`,
    });
  };

  const handleSelectAll = () => {
    const meta = getMeta();
    const uMap = { ...userMap };
    if (meta.allSelected) {
      for (let k in uMap) {
        set(uMap, [k, 'selectedTasks'], []);
      }
    } else {
      for (let k in uMap) {
        set(
          uMap,
          [k, 'selectedTasks'],
          get(uMap, [k, 'tasks'], []).map((t) => t.id)
        );
      }
    }
    setUserMap(uMap);
  };

  const handleToggle = (e, item) => {
    stopProp(e);

    const uMap = { ...userMap };
    const obj = get(uMap, item.key);

    if (size(obj.selectedTasks) >= size(obj.tasks)) {
      obj.selectedTasks = [];
    } else {
      obj.selectedTasks = obj.tasks.map((t) => t.id);
    }
    setUserMap(uMap);
  };

  const handleToggleOpen = (e, item) => {
    stopProp(e);

    const id = get(item, 'key');
    const uMap = { ...userMap };
    const obj = get(uMap, id);
    set(obj, 'open', get(obj, 'open') ? false : true);
    setUserMap(uMap);
  };

  const toggleTaskSelect = (e, item, task) => {
    stopProp(e);
    const uMap = { ...userMap };

    const tasks = get(uMap, [item.key, 'selectedTasks'], []);
    const taskIndex = tasks.indexOf(task.id);
    if (taskIndex > -1) {
      tasks.splice(taskIndex, 1);
    } else {
      tasks.push(task.id);
    }
    set(uMap, [item.key, 'selectedTasks'], tasks);
    setUserMap(uMap);
  };

  const getMeta = () => {
    const obj = {
      somethingSelected: false,
      allSelected: true,
      meSelected: false,
    };

    for (let k in userMap) {
      const uobj = userMap[k];

      const selTasks = get(uobj, 'selectedTasks', []);
      if (size(selTasks)) {
        if (get(uobj, 'key') === me.id) {
          obj.meSelected = true;
        }
        obj.somethingSelected = true;
        if (size(selTasks) < size(uobj.tasks)) {
          obj.allSelected = false;
        }
      } else {
        obj.allSelected = false;
      }
    }
    return obj;
  };

  const meta = getMeta();
  const somethingSelected = meta.somethingSelected;

  return (
    <Dialog className="notify-modal-dialog" fullScreen disableEscapeKeyDown open={props.open} onClose={handleClose}>
      <StyledAppBar color="secondary">
        <StyledToolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label="Close"
            size="large">
            <CloseIcon />
          </IconButton>

          <BarTitle>
            <Title variant="h5">
              New Task Notifications
            </Title>
          </BarTitle>
        </StyledToolbar>
      </StyledAppBar>

      <Container>
        {error && (
          <Alert
            severity="error"
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setError();
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {error}
          </Alert>
        )}

        <NotifyList as={List}>
          <SelectAll key={'all'} role={undefined} dense onClick={handleSelectAll}>
            <CheckboxIcon>
              <Checkbox edge="start" checked={meta.allSelected} />
            </CheckboxIcon>
            <ListItemText primary={meta.allSelected ? 'Deselect All' : `Select All`} />
          </SelectAll>
          {assignees.map((item, i) => {
            const { key, tasks } = item;
            const id = key;
            const obj = get(userMap, id);
            const isOpen = get(obj, 'open');
            const taskCount = size(obj.tasks);
            const selectedTaskCount = size(obj.selectedTasks);
            const indeterminate = selectedTaskCount && selectedTaskCount < taskCount ? true : false;
            return (
              <React.Fragment key={id}>
                <ListItemButton role={undefined} onClick={(e) => handleToggle(e, item)}>
                  <CheckboxIcon>
                    <Checkbox edge="start" indeterminate={indeterminate} checked={!!selectedTaskCount} />
                  </CheckboxIcon>
                  <AssigneeText as={ListItemText}
                    primary={`Assigned To: ${item.assignees
                      .map((a) => {
                        return a.id === me.id ? 'Me' : a.name;
                      })
                      .join(', ')}`}
                  />
                  <SecondaryAction>
                    <SecondaryButton endIcon={<Icon>{isOpen ? 'arrow_drop_up' : 'arrow_drop_down'}</Icon>} onClick={(e) => handleToggleOpen(e, item)} size="small">
                      {`${size(item.tasks)} Tasks`}
                    </SecondaryButton>
                  </SecondaryAction>
                </ListItemButton>

                <Collapse in={isOpen} unmountOnExit>
                  <ChildList as={List} disablePadding>
                    {(tasks || []).map((task, i) => {
                      const sel = obj.selectedTasks.indexOf(task.id) > -1;
                      const image = getTaskImage(task);
                      return (
                        <StyledListItem key={task.id} dense onClick={(e) => toggleTaskSelect(e, item, task)}>
                          <CheckboxIcon>
                            <Checkbox edge="start" checked={sel} />
                          </CheckboxIcon>
                          <ListItemText
                            primary={get(task, 'name', '')}
                            secondary={getTaskStatus(task).title}
                            sx={image.src ? { maxWidth: 'calc(100% - 76px)' } : undefined}
                          />
                          {image.src && (
                            <SecondaryAction>
                              <div style={{ position: 'relative', width: 60, height: 60 }}>
                                <Image
                                  src={image.src}
                                  width={60}
                                  height={60}
                                  autosize={0}
                                  useNaturalDims={false}
                                  imageStyle={{ objectFit: 'cover', borderRadius: 4 }}
                                />
                                {image.icon && <ImageIconWrap as={Icon}>{image.icon}</ImageIconWrap>}
                              </div>
                            </SecondaryAction>
                          )}
                        </StyledListItem>
                      );
                    })}
                  </ChildList>
                </Collapse>
              </React.Fragment>
            );
          })}
        </NotifyList>

        <ButtonWrap>
          <Button
            onClick={reassign}
            startIcon={loading ? <Loader size={16} classes={{ svg: NOTIFY_WHITE_ICON }} /> : <Icon>person_add</Icon>}
            variant="contained"
            color="secondary"
            disabled={loading || !somethingSelected}
          >
            RE-ASSIGN
          </Button>
          <ButtonRow>
            <Button variant="outlined" color="secondary" startIcon={<Icon>cancel</Icon>} onClick={dontNotify} disabled={!somethingSelected}>
              {`DON'T NOTIFY`}
            </Button>

            <Button variant="contained" color="secondary" startIcon={<Icon>link</Icon>} onClick={share} disabled={!somethingSelected}>
              {`SHARE LINK`}
            </Button>

            <Button
              variant="contained"
              color="primary"
              startIcon={<Icon>{sending ? <Loader size={20} classes={{ svg: NOTIFY_WHITE_ICON }} /> : 'send'}</Icon>}
              onClick={notify}
              disabled={!somethingSelected || meta.meSelected}
            >
              {`NOTIFY ASSIGNEES`}
            </Button>
          </ButtonRow>
        </ButtonWrap>
      </Container>

      <ReassignModal {...reModal} />
    </Dialog>
  );
};
